<template>
    <section class="table-standard">
        <article class="row">

            <AlertComponent
                :type="alertType"
                :message="alertMessage"
                @close="alertMessage = null"
            />

            <div class="view-mode">
                <div class="left">
                    <div class="mode">
                        <template v-for="button in buttons">
                            <router-link
                                v-if="button.url"
                                :to="addQueryString(button.url)"
                                :class="`btn rounded-full small uppercase ${button.class || ''}`"
                            >
                                <i :class="`bi bi-${button.icon}`"></i> {{ button.text }}
                            </router-link>
                            <button
                                v-else-if="selectedRows.length > 0 && button.type === 'delete' && this.apiUrl"
                                :class="`btn rounded-full small uppercase red ${button.class || ''}`"
                                @click="deleteSelectedItems(button.field || 'id')"
                            >
                                <i :class="`bi bi-${button.icon || 'trash'}`"></i> {{ button.text || 'Excluir Selecionados' }}
                            </button>
                            <button
                                v-else-if="!button.type && button.action"
                                :class="`btn rounded-full small uppercase ${button.class || ''}`"
                                @click="button.action"
                            >
                                <i :class="`bi bi-${button.icon}`"></i> {{ button.text }}
                            </button>
                        </template>
                        <div class="separator"></div>
                        <button class="btn rounded-small small icon inverse inverse-hover" @click="fetchData()" title="Atualizar"><i class="bi bi-arrow-clockwise"></i></button>
                        <button class="btn rounded-small small icon inverse inverse-hover" @click="toggleCardMode()" title="Mudar modo de visualização">
                            <i :class="viewMode === 'card' ? 'bi bi-grid-3x3-gap-fill' : 'bi bi-list-ul'"></i>
                        </button>
                    </div>                    
                </div>
                <div class="fields">
                    <div class="separator-right">
                        <div ref="columns" :class="['columns', isColumnsActive ? 'active' : '']">
                            <button @click.stop="toggleColumns" class="btn rounded-small small icon inverse inverse-hover" title="Colunas visíveis"><i class="bi bi-ui-checks-grid"></i></button>
                            <div class="items" @click.stop>
                                <span class="title">Colunas visíveis:</span>
                                <button 
                                    v-for="column in localColumns.filter(col => col.key !== 'actions')" 
                                    :key="column.key" 
                                    :class="['btn rounded-small small inverse', column.show ? 'actived' : 'inactive']"
                                    @click="toggleColumnVisibility(column)"
                                >
                                    <i 
                                        v-if="column.sortOrder" 
                                        :class="`bi bi-sort-alpha-${column.sortOrder === 'ASC' ? 'down' : 'up-alt'}`"
                                    ></i>
                                    {{ column.label }}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="filters">
                        <form @submit.prevent="applyFilters" class="form-standard">
                            <div class="columns stretched slim">
                                <component
                                    v-for="(filter, key) in getFirstFilter()"
                                    :is="filter.type === 'select' ? 'SelectField' : 'InputField'"
                                    :key="key"
                                    :type="filter.type"
                                    :label="filter.label"
                                    :name="key"
                                    :value="filter.value"
                                    :class="filter.class + ' bg black slim'"
                                    :tip="filter.tip"
                                    :mask-type="filter.maskType"
                                    :placeholder="filter.placeholder"
                                    :maxlength="filter.maxlength"
                                    :options="filter.type === 'select' ? filter.options : undefined"
                                    @value-updated="updateFilterValue"
                                />
                                <div :class="['buttons', 'auto', 'small']">
                                    <button class="btn rounded-small small uppercase filter" type="submit"><i class="bi bi-search"></i> Filtrar</button>
                                    <button 
                                        v-if="Object.keys(filters).length > 1"
                                        class="btn rounded-small small toggle-filters transparent no-border" 
                                        type="button" 
                                        @click="toggleFilters"
                                    >
                                        <i :class="`bi bi-${showAllFilters ? 'dash' : 'plus'}-lg`"></i>
                                    </button>
                                    <span 
                                        v-if="Object.keys(dataFilter).filter(key => key !== 'sort').slice(1).some(key => !showAllFilters && dataFilter[key])"
                                        class="filter-indicator"
                                        title="Existem filtros aplicados que estão ocultos"
                                        @click.prevent
                                    >
                                        <i class="bi bi-exclamation-diamond-fill"></i> Filtros ocultos aplicados
                                    </span>
                                </div>
                            </div>
                            <div class="columns stretched slim margin-top-big">
                                <component
                                    v-for="(filter, key) in getOthersFilters()"
                                    :is="filter.type === 'select' ? 'SelectField' : 'InputField'"
                                    :key="key"
                                    :type="filter.type"
                                    :label="filter.label"
                                    :name="key"
                                    :value="filter.value"
                                    :class="filter.class + ' bg black slim'"
                                    :tip="filter.tip"
                                    :mask-type="filter.maskType"
                                    :placeholder="filter.placeholder"
                                    :maxlength="filter.maxlength"
                                    :options="filter.type === 'select' ? filter.options : undefined"
                                    @value-updated="updateFilterValue"
                                    v-show="showAllFilters"
                                />
                            </div>
                        </form>
                    </div>
                </div>
                <div class="qty-view">
                    <SelectField
                        :options="[
                            { value: '20', label: '20', selected: maxPerPage === 20 },
                            { value: '50', label: '50', selected: maxPerPage === 50 },
                            { value: '100', label: '100', selected: maxPerPage === 100 },
                            { value: '500', label: '500', selected: maxPerPage === 500 }
                        ]"
                        name="view"
                        class="table-input text-center bg black slim"
                        label="Máx."
                        required="required"
                        @change="changeMaxPerPage($event.target.value)"
                    />
                </div>
                <div class="navigator">
                    <div class="v-align">
                        <span>
                            {{ totalItems === 0 ? '0-0 de 0' : `${(currentPage - 1) * maxPerPage + 1}-${Math.min(currentPage * maxPerPage, totalItems)} de ${totalItems}` }}
                        </span>
                        <button 
                            class="btn rounded-small small icon disabled-secondary" 
                            :disabled="currentPage === 1" 
                            @click="changePage(currentPage - 1)"
                        >
                            <i class="bi bi-chevron-left"></i>
                        </button>
                        <button 
                            class="btn rounded-small small icon disabled-secondary" 
                            :disabled="currentPage === totalPages" 
                            @click="changePage(currentPage + 1)"
                        >
                            <i class="bi bi-chevron-right"></i>
                        </button>
                    </div>
                </div>
            </div>

            <table :class="viewMode === 'card' ? 'card-mode' : ''">
                <thead>
                    <tr>
                        <!-- Checkbox para selecionar todas as linhas -->
                        <th class="check-row w-1" v-if="selectRows">
                            <label>
                                <input
                                    type="checkbox"
                                    v-model="selectAll"
                                    @change="toggleSelectAll"
                                />
                            </label>
                        </th>
                        <th
                            v-for="column in localColumns"
                            :key="column.key"
                            :class="[column.class, column.sortable ? 'sortable' : '', column.show ? '' : 'hidden']"
                            @click="column.sortable ? sortColumn(column) : null"
                        >
                            <span>{{ column.label }}</span>
                            <span v-if="column.sortable && column.sortOrder" class="sort-icon">                         
                                <i :class="`bi bi-sort-alpha-${column.sortOrder === 'DESC' ? 'up-alt' : 'down'}`"></i>
                                <span v-if="column.sortOrder && localColumns.filter(col => col.sortOrder).length > 1">{{ column.sortPriority }}</span>
                            </span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="isEmpty">
                        <td :colspan="localColumns.length + (selectRows ? 1 : 0)" class="empty-message text-center">
                            <span>Nenhum item encontrado. Tente ajustar os filtros ou adicionar novos dados.</span>
                        </td>
                    </tr>
                    <tr v-else v-for="(item, index) in (isLoading ? skeletonData : data)" :key="index">
                        <!-- Checkbox para selecionar uma linha -->
                        <td class="check-row w-1" v-if="selectRows">
                            <label>
                                <input
                                    type="checkbox"
                                    :checked="selectedRows.includes(index)"
                                    @change="toggleRowSelection(index)"
                                    :disabled="isLoading"
                                />
                            </label>
                        </td>
                        <td
                            v-for="column in localColumns"
                            :key="column.key"
                            :class="[column.class, isLoading ? 'skeleton' : '', column.show ? '' : 'hidden']"
                        >
                            <template v-if="isLoading">
                                <span class="skeleton-placeholder"></span> <!-- Placeholder para o skeleton -->
                            </template>
                            <template v-else-if="column.key === 'actions'">
                                <span>
                                    <router-link
                                        v-for="action in actions"
                                        :key="action.text"
                                        :class="`btn rounded-small small ${action.class} tooltip`"
                                        :title="action.text"
                                        :to="action.url ? handleAction(addQueryString(action.url), item) : null"
                                        :target="action.target || null"
                                    ><i :class="`bi bi-${action.icon}`"></i>{{ action.text }}</router-link>
                                </span>
                            </template>
                            <template v-else>
                                <span
                                    v-if="column.type === 'image'"
                                    :style="{ backgroundImage: `url('${item[column.key] || column.default || ''}')` }"
                                ></span>
                                <span v-else>{{ item[column.key] }}</span>
                            </template>
                        </td>
                    </tr>
                </tbody>
            </table>

            <div v-if="showPagination()" class="pagination">
                <button 
                    class="btn rounded-small small" 
                    :disabled="currentPage === 1" 
                    @click="changePage(1)"
                >
                    <i class="bi bi-chevron-double-left"></i>
                </button>
                <button 
                    class="btn rounded-small small" 
                    :disabled="currentPage === 1" 
                    @click="changePage(currentPage - 1)"
                >
                    <i class="bi bi-chevron-left"></i>
                </button>
                <span>{{ currentPage }} / {{ totalPages }}</span>
                <button 
                    class="btn rounded-small small" 
                    :disabled="currentPage === totalPages" 
                    @click="changePage(currentPage + 1)"
                >
                    <i class="bi bi-chevron-right"></i>
                </button>
                <button 
                    class="btn rounded-small small" 
                    :disabled="currentPage === totalPages" 
                    @click="changePage(totalPages)"
                >
                    <i class="bi bi-chevron-double-right"></i>
                </button>
            </div>
        </article>
    </section>
    <ModalComponent
        :show="showDeleteModal"
        :title="'Excluir itens'"
        :message="'Deseja realmente excluir os itens selecionados?'"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
    />
    <ModalComponent
        :show="showColumnsModal"
        :title="'Atenção'"
        :message="columnsModalMessage"
        @confirm="confirmColumnsShow"
        @cancel="cancelColumnsShow"
        :cancelText="'Manter ordenação'"
        :confirmText="'Remover ordenação'"
    />
</template>

<script>
import axios from '@/store/axios';
import AlertComponent from '@/components/general/AlertComponent.vue';
import ModalComponent from '@/components/general/ModalComponent.vue';

export default {
    name: 'TableComponent',
    components: {
        AlertComponent,
        ModalComponent,
    },
    props: {
        selectRows: {
            type: Boolean,
            default: false
        },
        buttons: {
            type: Array,
            default: () => []
        },
        filters: {
            type: Object,
            required: true
        },
        columns: {
            type: Array,
            required: true
        },
        actions: {
            type: Array,
            default: () => []
        },
        apiUrl: {
            type: String,
            default: null
        },
    },
    data() {
        return {
            data: null,
            selectAll: false,
            selectedRows: [],
            isLoading: true,
            skeletonData: Array.from({ length: 10 }, () => ({})),
            isEmpty: false,
            currentPage: 1,
            totalPages: 1,
            maxPerPage: this.initialMaxItems(),
            totalItems: 0,
            abortController: null,
            dataFilter: this.initializeDataFilter(),
            localColumns: [],
            isColumnsActive: false,
            showAllFilters: false,
            alertMessage: null,
            alertType: null,
            viewMode: 'table',
            showDeleteModal: false,
            itemsToDelete: [],
            showColumnsModal: false,
            columnsModalMessage: '',
            columnCurrent: null,
        };
    },
    updated() {
        this.updateLoading();
    },
    mounted() {
        // Adiciona a coluna 'actions' dinamicamente se houver ações
        if (this.actions && this.actions.length > 0 && !this.columns.some(col => col.key === 'actions')) {
            const actionCount = this.actions.length || 1;
            const widthClass = `w-${actionCount * 2}`;
            this.columns.push({ key: 'actions', label: 'Ações', class: `actions ${widthClass}`, show: true });
        }

        // update skeletonData length based on maxPerPage prop
        const skeletonCount = Math.min(parseInt(this.maxPerPage, 10) || 1, 10);
        this.skeletonData = Array.from({ length: skeletonCount }, () => ({})),

        this.localColumns = this.columns.map(column => ({ ...column }));

        // Carrega os filtros da query string, se existirem
        const queryFilters = this.$route.query;
        Object.keys(this.filters).forEach(key => {
            if (queryFilters[key] !== undefined) {
                this.filters[key].value = queryFilters[key];
                this.dataFilter[key] = queryFilters[key];
            }
        });
        
        // Carrega a ordenação da query string, se existir
        if (queryFilters.sort) {
            const sortColumns = queryFilters.sort.split(',').map(sortKey => {
                const isDescending = sortKey.startsWith('-');
                const key = isDescending ? sortKey.slice(1) : sortKey;
                return { key, sortOrder: isDescending ? 'DESC' : 'ASC' };
            });

            sortColumns.forEach((sortColumn, index) => {
                const column = this.localColumns.find(col => col.key === sortColumn.key);
                if (column) {
                    column.sortOrder = sortColumn.sortOrder;
                    column.sortPriority = index + 1;
                }
            });

            this.dataFilter.sort = queryFilters.sort; // Atualiza o filtro de ordenação
        }

        // Carrega as colunas visíveis da query string, se existirem
        const queryVisibleColumns = this.$route.query.visible_columns;
        if (queryVisibleColumns) {
            const visibleColumns = queryVisibleColumns.split(',');
            this.localColumns.forEach(column => {
                column.show = visibleColumns.includes(column.key);
            });
        }

        // Verifica se a query 'page' está presente e atualiza a página atual
        const queryPage = parseInt(this.$route.query.page, 10);
        if (!isNaN(queryPage) && queryPage >= 1) {
            this.currentPage = queryPage;
        }

        // Verifica se a query 'maxPerPage' está presente e atualiza o valor
        const queryMaxPerPage = parseInt(this.$route.query.max_items, 10);
        if (!isNaN(queryMaxPerPage) && queryMaxPerPage > 0) {
            this.maxPerPage = queryMaxPerPage;
            // console.log('maxPerPage', this.maxPerPage);
        }

        this.fetchData();
    },
    methods: {
        initialMaxItems() {
            const maxItems = parseInt(this.$route.query.max_items, 10);
            return !isNaN(maxItems) && maxItems > 0 ? maxItems : 20;
        },
        initializeDataFilter() {
            const initialDataFilter = {};
            Object.keys(this.filters).forEach(key => {
                initialDataFilter[key] = this.filters[key].value || ''; // Inicializa com valor padrão ou vazio
            });
            return initialDataFilter;
        },
        updateFilterValue(field){
            this.dataFilter[field.key] = field.value;
        },
        fetchData() {
            // Cancela a requisição anterior, se existir
            if (this.abortController) {
                this.abortController.abort();
            }

            // Cria um novo controlador de abortamento
            this.abortController = new AbortController();
            const signal = this.abortController.signal;

            this.data = null;
            this.selectAll = false;
            this.selectedRows = [];
            // setTimeout(() => {
                // Carrega os dados da API se a URL for fornecida
                if(this.apiUrl){
                    const apiUrl = this.apiUrl;

                    // Adiciona os filtros como parâmetros
                    const filterParams = Object.entries(this.dataFilter).reduce((acc, [key, value]) => {
                        acc[key] = value;
                        return acc;
                    }, {});

                    const params = {
                        page: this.currentPage,
                        max_items: this.maxPerPage,
                        ...filterParams,
                    };

                    axios.get(apiUrl, {
                        params,
                        signal: signal, // Passa o sinal de abortamento para a requisição
                    })
                    .then(response => {
                        const data = response.data?.data || null;
                        if (data) {

                            const pagination = response.data?.pagination;

                            if (pagination) {
                                this.totalPages = pagination?.total_pages || 1;
                                this.totalItems = pagination?.total_results || 0;

                                // Verifica se a página atual é maior que o total de páginas
                                if (this.currentPage > this.totalPages) {
                                    this.currentPage = 1; // Reseta para a página 1
                                    this.updateQueryString(); // Atualiza a query string
                                    this.fetchData(); // Recarrega os dados
                                    return;
                                }
                            }

                            this.data = data.map(item => ({
                                ...this.columns.reduce((acc, column) => {
                                    const keys = column.key.split('.'); // Divide a chave em partes para acessar subobjetos
                                    let value = item;
                                    for (const key of keys) {
                                        if (value && value.hasOwnProperty(key)) {
                                            value = value[key]; // Navega pelos subobjetos
                                        } else {
                                            value = ''; // Caso a chave não exista, define como null
                                            break;
                                        }
                                    }
                                    acc[column.key] = this.processField(value, column.type || 'text');
                                    return acc;
                                }, {}),
                            }));
                        }
                        this.updateLoading();
                    })
                    .catch(error => {
                        if (axios.isCancel(error)) {
                            // console.log('Requisição cancelada:', error.message);
                        } else {
                            this.data = [];
                            this.updateLoading();
                        }
                    });
                }
                else {
                    this.data = [];
                }
            // }, 150); // Simula um atraso de 500ms para o carregamento dos dados
        },
        deleteSelectedItems(field = 'id') {
            if (this.selectedRows.length > 0) {
                // Armazena os IDs dos itens selecionados e exibe a modal
                this.itemsToDelete = this.selectedRows.map(index => this.data[index][field]);
                this.showDeleteModal = true;
            } else {
                this.alertType = 'warning';
                this.alertMessage = 'Nenhum item selecionado para exclusão.';
            }
        },
        confirmDelete() {
            if (this.apiUrl) {
                axios
                    .delete(this.apiUrl, { data: { ids: this.itemsToDelete } })
                    .then(response => {
                        const data = response.data?.data || null;
                        if (data) {
                            const successCount = data.success?.length || 0;
                            const errorCount = data.errors?.length || 0;

                            if (successCount > 0 && errorCount === 0) {
                                this.alertType = 'success';
                                this.alertMessage = `${successCount} ${successCount === 1 ? 'item' : 'itens'} excluído${successCount === 1 ? '' : 's'} com sucesso.`;
                            } else if (errorCount > 0 && successCount === 0) {
                                this.alertType = 'error';
                                this.alertMessage = `Erro ao excluir ${errorCount} ${errorCount === 1 ? 'item' : 'itens'}.`;
                            } else if (successCount > 0 && errorCount > 0) {
                                this.alertType = 'warning';
                                this.alertMessage = `${successCount} ${successCount === 1 ? 'item' : 'itens'} excluído${successCount === 1 ? '' : 's'} com sucesso, mas ${errorCount} falharam.`;
                            }
                        }
                        this.selectedRows = [];
                        this.fetchData();
                    })
                    .catch(error => {
                        this.alertType = 'error';
                        this.alertMessage = 'Erro ao excluir os itens.';
                    })
                    .finally(() => {
                        this.showDeleteModal = false; // Fecha a modal
                    });
            }
        },
        cancelDelete() {
            this.itemsToDelete = []; // Limpa os itens selecionados
            this.showDeleteModal = false; // Fecha a modal sem excluir
        },
        changePage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page;
                this.updateQueryString(); // Update the query string with the current page
                this.fetchData();
            }
        },
        changeMaxPerPage(maxPerPage) {
            const skeletonCount = Math.min(parseInt(maxPerPage, 10) || 1, 10);
            this.skeletonData = Array.from({ length: skeletonCount }, () => ({}));
            this.maxPerPage = maxPerPage;
            this.currentPage = 1; // Reinicia para a primeira página
            this.updateQueryString(); // Atualiza a query string com o novo maxPerPage
            this.fetchData();
        },
        updateQueryString() {
            // Atualiza a query string com os valores dos filtros, número da página e maxPerPage
            const visible_columns = this.localColumns
                .filter(column => column.show)
                .map(column => column.key)
                .join(',');
            const query = {
                ...this.$route.query,
                ...this.dataFilter,
                visible_columns,
                page: this.currentPage, // Adiciona a página atual
                max_items: this.maxPerPage, // Adiciona o maxPerPage
            };
            const path = this.$route.path; // Mantém o caminho atual com a barra no final
            this.$router.replace({ path, query });
        },
        applyFilters() {
            this.updateQueryString();
            this.fetchData();
        },
        toggleSelectAll() {
            if (!this.data) {
                return null;
            }
            if (this.selectAll) {
                // Seleciona todos os índices dos itens visíveis
                this.selectedRows = this.data.map((item, index) => index);
            } else {
                // Limpa a seleção
                this.selectedRows = [];
            }
        },
        handleAction(url, item) {
            if (url) {
                // Substitui os placeholders {campo} pelos valores correspondentes de item
                const parsedUrl = url.replace(/{([^}]+)}/g, (match, key) => {
                    return item[key] !== undefined ? item[key] : match; // Substitui pelo valor ou mantém o placeholder
                });

                // Divide a URL em path e query string
                const [path, queryString] = parsedUrl.split('?');

                // Converte a query string em um objeto, se existir
                const query = queryString
                    ? Object.fromEntries(new URLSearchParams(queryString))
                    : undefined;

                return {
                    path,
                    query,
                };
                // Faz o push com path e query
                // this.$router.push({ path, query });
            }
        },
        toggleCardMode() {
            if (this.viewMode === 'table') {
                this.viewMode = 'card';
            }
            else {
                this.viewMode = 'table';
            }
        },
        updateLoading() {
            // const currentTime = new Date().toLocaleTimeString();
            // console.log('updateLoading', this.data, 'at', currentTime);
            if (Array.isArray(this.data)) {
                this.isLoading = false;
                this.isEmpty = this.data.length ? false : true;
            } else {
                // console.log('Data is not an array:', this.data);
                this.isLoading = true;
            }
        },
        processField(value, type = 'text'){
            const type_real = type.split('[')[0];
            const params = type.split('[')[1]?.replace(']', '').split(',');
            switch (type_real) {
                case 'number':
                    const length = params && params[0] ? parseInt(params[0], 10) : 0;
                    return String(value).padStart(length, '0');
                case 'date':
                    const date = new Date(value);
                    if (isNaN(date.getTime())) {
                        return '';
                    }
                    return date.toLocaleDateString('pt-BR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                    });
                case 'status':
                    if (params && params.length > 0) {
                        const statusMap = params.reduce((acc, param) => {
                            const [key, value] = param.split(':').map(str => str.trim());
                            acc[key] = value;
                            return acc;
                        }, {});
                        return statusMap[value] || '';
                    }
                    return value ? 'Ativo' : 'Inativo';
                default:
                    return String(value).trim();
            }
        },
        showPagination() {
            return this.totalItems > this.maxPerPage;
        },
        toggleColumnVisibility(column) {
            if (column.sortOrder && column.show) {
                this.columnsModalMessage = `A coluna "${column.label}" está ordenada. Deseja remover a ordenação antes de ocultá-la?`;
                this.showColumnsModal = true;
                this.columnCurrent = column; // Armazena a coluna atual para referência na confirmação
            }
            column.show = !column.show;
            this.updateQueryString(); // Atualiza a query string com as colunas visíveis
        },
        cancelColumnsShow() {
            this.showColumnsModal = false;
        },
        confirmColumnsShow() {
            this.showColumnsModal = false;
            this.columnCurrent.sortOrder = null; // Remove a ordenação da coluna
            this.columnCurrent.sortPriority = null; // Remove a prioridade de ordenação
            this.fetchData(); // Recarrega os dados
            this.updateQueryString(); // Atualiza a query string com as colunas visíveis
        },
        toggleColumns() {
            this.isColumnsActive = !this.isColumnsActive;
            if (this.isColumnsActive) {
                document.addEventListener('click', this.handleOutsideClick);
            } else {
                document.removeEventListener('click', this.handleOutsideClick);
            }
        },
        handleOutsideClick(event) {
            const columnsElement = this.$refs.columns;
            if (columnsElement && !columnsElement.contains(event.target)) {
                this.isColumnsActive = false;
                document.removeEventListener('click', this.handleOutsideClick);
            }
        },
        toggleRowSelection(index) {
            const selectedIndex = this.selectedRows.indexOf(index);
            if (selectedIndex === -1) {
                this.selectedRows.push(index); // Adiciona o índice se não estiver presente
            } else {
                this.selectedRows.splice(selectedIndex, 1); // Remove o índice se já estiver presente
                this.selectAll = false; // Desmarca o checkbox "Selecionar todos" se algum item for desmarcado
            }
        },
        sortColumn(column) {
            // Inicializa a propriedade `sortOrder` se não existir
            if (!column.sortOrder) {
                column.sortOrder = 'ASC'; // Começa com ASC
            } else if (column.sortOrder === 'ASC') {
                column.sortOrder = 'DESC'; // Alterna para DESC
            } else {
                column.sortOrder = null; // Remove a ordenação
            }

            // Atualiza a prioridade de ordenação
            const activeSorts = this.localColumns
                .filter(col => col.sortOrder)
                .sort((a, b) => (a.sortPriority || 0) - (b.sortPriority || 0));

            if (column.sortOrder) {
                column.sortPriority = activeSorts.length + 1; // Define a prioridade como a última
            } else {
                column.sortPriority = null; // Remove a prioridade se não houver ordenação
            }

            // Recalcula as prioridades
            activeSorts.forEach((col, index) => {
                col.sortPriority = index + 1;
            });

            // Gera a string de ordenação para a API
            const sortQuery = this.localColumns
                .filter(col => col.sortOrder)
                .sort((a, b) => a.sortPriority - b.sortPriority)
                .map(col => (col.sortOrder === 'DESC' ? `-${col.key}` : col.key))
                .join(',');

            // Atualiza os filtros e refaz a requisição
            this.dataFilter.sort = sortQuery;
            this.updateQueryString();
            this.fetchData();
        },
        toggleFilters() {
            this.showAllFilters = !this.showAllFilters;
        },
        encodeQueryString() {
            const query = Object.entries(this.$route.query).reduce((acc, [key, value]) => {
                acc[key] = Array.isArray(value) ? value.join(',') : value;
                return acc;
            }, {});
            const queryString = new URLSearchParams(query).toString();
            return btoa(queryString);
        },
        addQueryString(url) {
            const queryString = this.encodeQueryString();
            if (url.includes('#')) {
                const [baseUrl, hash] = url.split('#');
                const separator = baseUrl.includes('?') ? '&' : '?';
                return `${baseUrl}${separator}query=${queryString}#${hash}`;
            } else if (url.includes('?')) {
                return `${url}&query=${queryString}`;
            } else {
                return `${url}?query=${queryString}`;
            }
        },
        getFirstFilter(){
            const keys = Object.keys(this.filters);
            if (keys.length > 0) {
                const firstKey = keys[0];
                return { [firstKey]: this.filters[firstKey] };
            }
            return {};
        },
        getOthersFilters(){
            const keys = Object.keys(this.filters);
            if (keys.length > 1) {
                const others = keys.slice(1).reduce((acc, key) => {
                    acc[key] = this.filters[key];
                    return acc;
                }, {});
                return others;
            }
            return {};
        }
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleOutsideClick);
    },
};
</script>

<style scoped src="@/assets/css/general/table-style.css"></style>
<style scoped src="@/assets/css/general/form-style.css"></style>